import * as React from 'react';
import PageLayout from '../SettingsPageLayout';
import { ReleaseComponentProps } from '../../typescriptHelpers';
import { TimelineEventStatus, TimelineEvent, ReleaseStepType } from '../../types';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../reducers/reducers';
import {
    selectAppPlatform,
    selectAppStoreType,
    selectShouldShowAutomationsTimelineEventsOnly,
    selectAllTrackingPropertiesForRelease,
    selectAppliedTimelineEventFiltersCount,
    selectIsWorkflowsCIStepEnabled,
    selectAppTimeFormatSetting,
    selectTimelineEventsFiltersMap,
    selectIsDemoInstance,
    selectRelease,
} from '../../selectors/selectors';
import { CSSTransition } from 'react-transition-group';
import { TimelineEventItem } from './TimelineEventItem';
import Text from 'antd/lib/typography/Text';
import Collapse from 'antd/lib/collapse';
import { CaretRightFilled, ThunderboltFilled, FilterOutlined } from '@ant-design/icons';
import { antGrey, titleStyle, runwayColors, tagStyle } from '../styles/globalStyles';
import dayjs from 'dayjs';
import Divider from 'antd/lib/divider';
import { formatDateString } from '@/helpers/displayHelpers';
import Input from 'antd/lib/input';
import type { SearchProps } from 'antd/lib/input/Search';
import { titleForEvent } from './TimelineEventItem';
import Tag from 'antd/lib/tag';
import { updateShowAutomationsTimelineEventsOnly } from '@/actions/filters';
import Button from 'antd/lib/button';
import Badge from 'antd/lib/badge';
import Popover from 'antd/lib/popover';
import { TimelineEventsFiltersComponent } from './TimelineEventsFilters';
import Spin from 'antd/lib/spin';
import { ReleaseTimelineSectionContext } from './EventsTimeline';
import { trackReleaseRouteEvent } from '../../actions/trackEvent';
import * as AnalyticEvents from '../../utils/analytics';
import { AnyTimelineEventsFilter, filterTimelineEvent } from '../../types/timelineEventsFilters';
import { Space } from 'antd/lib';
import { useTimelineEventsPagination } from '@/utils/hooks/useTimelineEventsPagination';
import { globalMessage } from '@/utils/globalMessage';

interface TimelineProps extends ReleaseComponentProps {
    events: TimelineEvent[];
    releaseStepType?: ReleaseStepType;
    isDrawerOpen: boolean;
    loadMore?: () => void;
    isLoading?: boolean;
    hasMore?: boolean;
}

export const Timeline: React.FC<TimelineProps> = ({
    appId,
    orgId,
    releaseId,
    events,
    releaseStepType,
    isDrawerOpen,
    loadMore,
    isLoading,
    hasMore,
}) => {
    const dispatch = useDispatch();
    const appPlatform = useSelector((state: RootState) => selectAppPlatform(state, appId));
    const appStoreType = useSelector((state: RootState) => selectAppStoreType(state, appId));
    const showAutomationsOnly = useSelector((state: RootState) => selectShouldShowAutomationsTimelineEventsOnly(state));
    const appliedFiltersCount = useSelector((state: RootState) => selectAppliedTimelineEventFiltersCount(state));
    const platform = useSelector((state: RootState) => selectAppPlatform(state, appId));
    const allReleaseTrackingProperties = useSelector((state: RootState) =>
        selectAllTrackingPropertiesForRelease(state, orgId, appId, releaseId),
    );

    const [timelineEvents, setTimelineEvents] = React.useState(events);
    const [eventSearchValue, setEventSearchValue] = React.useState('');
    const [filtersVisible, setFiltersVisible] = React.useState(false);

    const upcomingTargetDateEvents = timelineEvents?.filter((event) => event.status === TimelineEventStatus.Target);
    const upcomingEvents = timelineEvents?.filter((event) => event.status === TimelineEventStatus.Upcoming) || [];

    const recurringEvents = timelineEvents?.filter((event) => event.status === TimelineEventStatus.Recurring) || [];
    const pastEvents =
        timelineEvents?.filter(
            (event) =>
                event.status !== TimelineEventStatus.Upcoming &&
                event.status !== TimelineEventStatus.Target &&
                event.status !== TimelineEventStatus.Recurring,
        ) || [];
    let today = dayjs();
    let lastRecurringEventDate = upcomingEvents?.length ? dayjs.unix(0) : null;
    let lastPastEventsDate = null;
    if (pastEvents?.length) {
        lastPastEventsDate = dayjs.unix(0);
        if (lastRecurringEventDate && today && lastRecurringEventDate.isSame(today, 'day')) {
            lastPastEventsDate = dayjs(pastEvents[0].triggeredAt);
        }
    }
    const lastWeekStart = today.subtract(7, 'days');
    const shouldLoadMore =
        hasMore && !isLoading && appliedFiltersCount === 0 && !showAutomationsOnly && !eventSearchValue?.length;

    const { CheckableTag } = Tag;
    const handleOnClickShowAutomationsOnly = (checked) => {
        dispatch(updateShowAutomationsTimelineEventsOnly(checked));
        AnalyticEvents.track(AnalyticEvents.Event.ApplyShowAutomationEventsOnly, allReleaseTrackingProperties);
    };

    React.useEffect(() => {
        setTimelineEvents(events);
        onSearch(eventSearchValue);
    }, [events]);

    const { Search } = Input;
    const isWorkflowsCIStepEnabled = useSelector((state: RootState) => selectIsWorkflowsCIStepEnabled(state, appId));
    const use24HourTimeFormat = useSelector((state: RootState) => selectAppTimeFormatSetting(state, appId));

    const onSearch: SearchProps['onSearch'] = (value, _e) => {
        const eventsOnSearch = events.filter((event) => {
            const title = titleForEvent({
                timelineEvent: event,
                appPlatform: appPlatform,
                appStoreType: appStoreType,
                isWorkflowsCIStepEnabled: isWorkflowsCIStepEnabled,
                use24HourTimeFormat,
            });
            return title?.toLowerCase().includes(value.toLowerCase());
        });

        setTimelineEvents(eventsOnSearch);
        setEventSearchValue(value);
    };

    const onApplyFilters = (filters: AnyTimelineEventsFilter[]) => {
        setFiltersVisible(false);

        // track applied filters
        const appliedFilters = filters.map((filter) => {
            return {
                type: filter.type,
            };
        });
        const allProperties = {
            ...allReleaseTrackingProperties,
            filters: appliedFilters,
        };
        AnalyticEvents.track(AnalyticEvents.Event.ApplyTimelineFilters, allProperties);
    };

    const shouldShowEmptyState =
        upcomingTargetDateEvents.length === 0 && upcomingEvents.length === 0 && pastEvents.length === 0;

    return (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Search placeholder="Search" allowClear onSearch={onSearch} style={{ width: 300 }} />
                <span>
                    <CheckableTag
                        checked={showAutomationsOnly}
                        onChange={handleOnClickShowAutomationsOnly}
                        style={{
                            color: showAutomationsOnly ? runwayColors.white : runwayColors.black,
                            borderColor: showAutomationsOnly ? runwayColors.primary : antGrey.grey5,
                            borderStyle: 'solid',
                            backgroundColor: showAutomationsOnly ? runwayColors.primary : runwayColors.white,
                            marginRight: 3,
                        }}
                    >
                        <ThunderboltFilled
                            style={{
                                color: showAutomationsOnly ? runwayColors.white : antGrey.grey7,
                                marginRight: 5,
                            }}
                        />
                        Show automations only
                    </CheckableTag>
                    <Divider type="vertical" style={{ height: 30, marginRight: 12 }}></Divider>
                    <Badge
                        count={appliedFiltersCount > 0 ? appliedFiltersCount : null}
                        style={{
                            backgroundColor: runwayColors.primary,
                            borderColor: runwayColors.primary,
                            color: 'white',
                            fontSize: 13,
                        }}
                    >
                        <Popover
                            overlayClassName="filters-overlay"
                            destroyTooltipOnHide
                            content={
                                <TimelineEventsFiltersComponent
                                    appId={appId}
                                    isVisible={filtersVisible}
                                    onApplyFilters={onApplyFilters}
                                    platform={platform}
                                    appStoreType={appStoreType}
                                    releaseStepType={releaseStepType}
                                />
                            }
                            title={
                                <Text style={{ ...tagStyle, fontSize: 12, color: antGrey.grey7, paddingLeft: '10px' }}>
                                    Filters
                                </Text>
                            }
                            trigger="click"
                            open={filtersVisible}
                            placement="left"
                            onOpenChange={(open) => setFiltersVisible(open)}
                        >
                            <Button icon={<FilterOutlined style={{ color: runwayColors.primary }} />} type="default">
                                Filter
                            </Button>
                        </Popover>
                    </Badge>
                </span>
            </div>
            {shouldShowEmptyState && (
                <Text type="secondary" style={{ marginTop: 15 }}>
                    No timeline events found
                </Text>
            )}
            <Collapse
                ghost
                bordered={false}
                defaultActiveKey={['1', '2']}
                expandIcon={({ isActive }) => (
                    <CaretRightFilled
                        style={{
                            color: antGrey.grey6,
                            fontSize: 24,
                        }}
                        rotate={isActive ? 90 : 0}
                    />
                )}
            >
                <div style={{ marginTop: 40, marginLeft: 16 }}>
                    {upcomingTargetDateEvents.map((timelineEvent) => {
                        const key = timelineEvent.triggeredAt + timelineEvent.type;
                        return (
                            <>
                                <CSSTransition key={key} timeout={500} classNames="timeline-event">
                                    <TimelineEventItem
                                        timelineEvent={timelineEvent}
                                        orgId={orgId}
                                        appId={appId}
                                        appPlatform={appPlatform}
                                        appStoreType={appStoreType}
                                        releaseStepType={isDrawerOpen ? null : releaseStepType}
                                        context={ReleaseTimelineSectionContext.ReleaseStep}
                                    />
                                </CSSTransition>
                            </>
                        );
                    })}
                </div>
                {recurringEvents?.length && (
                    <Collapse.Panel
                        key={'1'}
                        header={
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <Text style={{ fontSize: 16, color: antGrey.grey8 }}>Recurring</Text>
                                <span
                                    style={{
                                        flexGrow: 1,
                                        marginLeft: 20,
                                        borderBottom: `1px solid ${antGrey.grey5}`,
                                    }}
                                />
                            </div>
                        }
                        style={{ marginTop: 25 }}
                    >
                        {recurringEvents.map((timelineEvent) => {
                            const key = timelineEvent.triggeredAt + timelineEvent.type;
                            const triggeredAtDate = dayjs(timelineEvent.triggeredAt);
                            const shouldShowDateDivider =
                                !!lastRecurringEventDate && !lastRecurringEventDate.isSame(triggeredAtDate, 'day');
                            const dateDividerString = formatDateString(timelineEvent.triggeredAt, 'MMM D');
                            lastRecurringEventDate = triggeredAtDate;

                            return (
                                <>
                                    {shouldShowDateDivider && (
                                        <Divider dashed style={{ borderColor: antGrey.grey5 }}>
                                            <Text style={{ ...titleStyle, fontWeight: 400, fontSize: 12 }}>
                                                {dateDividerString}
                                            </Text>
                                        </Divider>
                                    )}
                                    <CSSTransition key={key} timeout={500} classNames="timeline-event">
                                        <TimelineEventItem
                                            timelineEvent={timelineEvent}
                                            orgId={orgId}
                                            appId={appId}
                                            appPlatform={appPlatform}
                                            appStoreType={appStoreType}
                                            releaseStepType={isDrawerOpen ? null : releaseStepType}
                                            context={ReleaseTimelineSectionContext.ReleaseStep}
                                        />
                                    </CSSTransition>
                                </>
                            );
                        })}
                    </Collapse.Panel>
                )}
                {upcomingEvents?.length && (
                    <Collapse.Panel
                        key={'1'}
                        header={
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <Text style={{ fontSize: 16, color: antGrey.grey8 }}>Upcoming</Text>
                                <span
                                    style={{
                                        flexGrow: 1,
                                        marginLeft: 20,
                                        borderBottom: `1px solid ${antGrey.grey5}`,
                                    }}
                                />
                            </div>
                        }
                        style={{ marginTop: 25 }}
                    >
                        {upcomingEvents.map((timelineEvent) => {
                            const key = timelineEvent.triggeredAt + timelineEvent.type;
                            const triggeredAtDate = dayjs(timelineEvent.triggeredAt);
                            const shouldShowDateDivider =
                                !!lastRecurringEventDate && !lastRecurringEventDate.isSame(triggeredAtDate, 'day');
                            const dateDividerString = formatDateString(timelineEvent.triggeredAt, 'MMM D');
                            lastRecurringEventDate = triggeredAtDate;

                            return (
                                <>
                                    {shouldShowDateDivider && (
                                        <Divider dashed style={{ borderColor: antGrey.grey5 }}>
                                            <Text style={{ ...titleStyle, fontWeight: 400, fontSize: 12 }}>
                                                {dateDividerString}
                                            </Text>
                                        </Divider>
                                    )}
                                    <CSSTransition key={key} timeout={500} classNames="timeline-event">
                                        <TimelineEventItem
                                            timelineEvent={timelineEvent}
                                            orgId={orgId}
                                            appId={appId}
                                            appPlatform={appPlatform}
                                            appStoreType={appStoreType}
                                            releaseStepType={isDrawerOpen ? null : releaseStepType}
                                            context={ReleaseTimelineSectionContext.ReleaseStep}
                                        />
                                    </CSSTransition>
                                </>
                            );
                        })}
                    </Collapse.Panel>
                )}
                {pastEvents.length > 0 && (
                    <Collapse.Panel
                        key={'2'}
                        header={
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <Text style={{ fontSize: 16, color: antGrey.grey8 }}>Past</Text>
                                <span
                                    style={{
                                        flexGrow: 1,
                                        marginLeft: 20,
                                        borderBottom: `1px solid ${antGrey.grey5}`,
                                    }}
                                />
                            </div>
                        }
                        style={{ marginTop: 25 }}
                    >
                        {pastEvents.map((timelineEvent) => {
                            const key = timelineEvent.triggeredAt + timelineEvent.type;
                            const triggeredAtDate = dayjs(timelineEvent.triggeredAt);
                            let shouldShowDateDivider = false;
                            let dateDividerString = formatDateString(timelineEvent.triggeredAt, 'MMM D');
                            if (lastPastEventsDate != null && !lastPastEventsDate.isSame(triggeredAtDate, 'day')) {
                                shouldShowDateDivider = true;
                                if (triggeredAtDate.isAfter(lastWeekStart) && today.isAfter(triggeredAtDate)) {
                                    dateDividerString = formatDateString(timelineEvent.triggeredAt, 'dddd, MMM D');
                                }
                            }
                            lastPastEventsDate = triggeredAtDate;

                            return (
                                <>
                                    {shouldShowDateDivider && (
                                        <Divider dashed style={{ borderColor: antGrey.grey5 }}>
                                            <Text style={{ ...titleStyle, fontWeight: 400, fontSize: 12 }}>
                                                {dateDividerString}
                                            </Text>
                                        </Divider>
                                    )}
                                    <CSSTransition key={key} timeout={500} classNames="timeline-event">
                                        <TimelineEventItem
                                            timelineEvent={timelineEvent}
                                            orgId={orgId}
                                            appId={appId}
                                            appPlatform={appPlatform}
                                            appStoreType={appStoreType}
                                            releaseStepType={isDrawerOpen ? null : releaseStepType}
                                            context={ReleaseTimelineSectionContext.ReleaseStep}
                                        />
                                    </CSSTransition>
                                </>
                            );
                        })}
                    </Collapse.Panel>
                )}
            </Collapse>

            {shouldLoadMore && (
                <div style={{ textAlign: 'center', marginTop: 16 }}>
                    <Button onClick={loadMore} loading={isLoading} type="default" size="small">
                        Load more events
                    </Button>
                </div>
            )}

            {isLoading && (
                <div style={{ textAlign: 'center', padding: '16px 0' }}>
                    <Space>
                        <Spin size="small" />
                    </Space>
                </div>
            )}
        </div>
    );
};

const TimelineEvents: React.FC<ReleaseComponentProps> = (props: ReleaseComponentProps) => {
    const releaseId = props.releaseId;
    const appId = props.appId;
    const orgId = props.orgId;
    const isDemoInstance = useSelector((state: RootState) => (orgId ? selectIsDemoInstance(state, orgId) : false));
    const release = useSelector((state: RootState) => (releaseId ? selectRelease(state, releaseId) : null));

    const paginationResult = useTimelineEventsPagination({
        orgId,
        appId,
        releaseId,
        initialLoad: !isDemoInstance,
    });

    const { events, isLoading, loadMore, error, isLoadingMore, paginationInfo } =
        isDemoInstance ?
            {
                events: release?.timelineEventsHistory || [],
                isLoading: false,
                error: false,
                loadMore: () => {},
                isLoadingMore: false,
                paginationInfo: { hasMore: false },
            }
        :   paginationResult;

    const currentFiltersMap = useSelector((state: RootState) => selectTimelineEventsFiltersMap(state));
    const shouldShowAutomationsOnly = useSelector((state: RootState) =>
        selectShouldShowAutomationsTimelineEventsOnly(state),
    );
    const filteredEvents = events.filter((event) =>
        filterTimelineEvent(event, currentFiltersMap, shouldShowAutomationsOnly),
    );

    React.useEffect(() => {
        if (error) {
            globalMessage.error('An error occurred when fetching release timeline events.');
        }
    }, [error]);

    if (isLoading) {
        return (
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    width: '100%',
                    height: '100vh',
                    flexGrow: 1,
                }}
            >
                <Spin />
            </div>
        );
    }

    return (
        <Timeline
            releaseId={props.releaseId}
            appId={props.appId}
            orgId={props.orgId}
            events={filteredEvents}
            isDrawerOpen={false}
            loadMore={loadMore}
            isLoading={isLoadingMore || isLoading}
            hasMore={paginationInfo?.hasMore}
        ></Timeline>
    );
};

const TimelineEventsContainer: React.FC<ReleaseComponentProps> = (props: ReleaseComponentProps) => {
    const dispatch = useDispatch();
    React.useEffect(() => {
        dispatch(
            trackReleaseRouteEvent({
                orgId: props.orgId,
                appId: props.appId,
                releaseId: props.releaseId,
                eventName: AnalyticEvents.Event.ViewReleaseTimeline,
            }),
        );
    }, []);
    return <PageLayout component={TimelineEvents} title="Timeline" description="" {...props} />;
};

export default TimelineEventsContainer;
